import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, fmtError, eur, TERRITOIRES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Trash2, Download, Save, Mail, PenLine } from "lucide-react";
import { toast } from "sonner";

const EMPTY_ITEM = { lot: "", designation: "", unite: "u", quantite: 1, prix_achat: 0, cout_mo: 0,
  cout_materiel: 0, cout_transport: 0, cout_sous_traitance: 0, autres_couts: 0, marge_pct: 20 };
const STATUTS = ["Brouillon", "Envoyé", "Relance", "Négociation", "Accepté", "Refusé"];

export default function QuoteEditor() {
  const { id } = useParams();
  const isNew = id === "nouveau";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [sites, setSites] = useState([]);
  const [prices, setPrices] = useState([]);
  const [q, setQ] = useState({
    client_id: "", client_nom: "", site_id: params.get("site") || "", territoire: "Guadeloupe",
    objet: "", date: "", tva_manuel: false, tva_taux: null, remise_pct: 0,
    conditions: "Devis valable 30 jours. Tarifs sous réserve de disponibilité.", statut: "Brouillon", items: [{ ...EMPTY_ITEM }],
  });
  const [saving, setSaving] = useState(false);
  const [signNom, setSignNom] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasStroke = useRef(false);

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/price-book").then((r) => setPrices(r.data)).catch(() => {});
    if (!isNew) api.get(`/quotes/${id}`).then((r) => setQ((prev) => ({ ...prev, ...r.data }))).catch((e) => toast.error(fmtError(e)));
  }, [id, isNew]);

  const set = (k, v) => setQ((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setQ((p) => { const items = [...p.items]; items[i] = { ...items[i], [k]: v }; return { ...p, items }; });

  const preview = useMemo(() => {
    let total = 0;
    q.items.forEach((it) => {
      const revient = (+it.prix_achat || 0) + (+it.cout_mo || 0) + (+it.cout_materiel || 0) + (+it.cout_transport || 0) + (+it.cout_sous_traitance || 0) + (+it.autres_couts || 0);
      total += revient * (1 + (+it.marge_pct || 0) / 100) * (+it.quantite || 0);
    });
    total *= 1 - (+q.remise_pct || 0) / 100;
    const taux = q.territoire === "Guyane" ? 0 : (q.tva_manuel && q.tva_taux != null ? +q.tva_taux : 8.5);
    return { ht: total, taux, tva: total * taux / 100, ttc: total * (1 + taux / 100) };
  }, [q]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...q, tva_taux: q.tva_manuel && q.tva_taux != null ? +q.tva_taux : null };
      const { data } = isNew ? await api.post("/quotes", payload) : await api.put(`/quotes/${id}`, payload);
      toast.success(`Devis ${data.numero} enregistré`);
      if (isNew) navigate(`/devis/${data.id}`, { replace: true });
      else setQ((p) => ({ ...p, ...data }));
    } catch (e) { toast.error(fmtError(e)); } finally { setSaving(false); }
  };

  const canvasPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const drawStart = (e) => {
    const ctx = canvasRef.current.getContext("2d");
    const p = canvasPos(e);
    ctx.strokeStyle = "#0B3D2E"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    drawing.current = true; hasStroke.current = true;
    e.target.setPointerCapture(e.pointerId);
  };
  const drawMove = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = canvasPos(e);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const drawEnd = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasStroke.current = false;
  };
  const sign = async () => {
    if (!signNom.trim() || !hasStroke.current) { toast.error("Nom complet et signature requis"); return; }
    try {
      await api.post(`/quotes/${id}/sign`, { nom: signNom, signature_data: canvasRef.current.toDataURL("image/png") });
      toast.success("Devis signé — statut Accepté");
      const { data } = await api.get(`/quotes/${id}`);
      setQ((p) => ({ ...p, ...data }));
    } catch (e) { toast.error(fmtError(e)); }
  };
  const sendEmail = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/send-email`, { to: emailTo || undefined });
      toast.success(data.message);
      setQ((p) => ({ ...p, statut: p.statut === "Brouillon" ? "Envoyé" : p.statut }));
    } catch (e) { toast.error(fmtError(e)); }
  };

  const addFromPriceBook = (i, ref) => {
    const p = prices.find((x) => x.reference === ref);
    if (p) { setItem(i, "designation", p.produit); setItem(i, "prix_achat", p.prix_ht); setItem(i, "marge_pct", p.prix_vente_ht && p.prix_ht ? Math.round((p.prix_vente_ht / p.prix_ht - 1) * 100) : 20); setItem(i, "unite", p.unite || "u"); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="quote-editor">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/devis" className="text-sm font-bold text-amber-600">← Devis</Link>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">{isNew ? "Nouveau devis" : q.numero}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isNew && (
            <>
              <a data-testid="quote-pdf-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${id}/pdf`}
                className="h-12 px-4 rounded-lg bg-teca-900 text-white font-bold text-sm flex items-center gap-2 hover:bg-slate-800">
                <Download className="h-4 w-4" /> PDF
              </a>
              <Input data-testid="quote-email-to" placeholder="email@client.fr" value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)} className="h-12 w-44" />
              <Button data-testid="quote-send-email" onClick={sendEmail} variant="outline" className="h-12 font-bold">
                <Mail className="h-4 w-4 mr-1" /> Envoyer
              </Button>
            </>
          )}
          <Button data-testid="quote-save" onClick={save} disabled={saving} className="h-12 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">
            <Save className="h-4 w-4 mr-1" /> {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Client</label>
          <select data-testid="quote-client" value={q.client_id} onChange={(e) => {
            const c = contacts.find((x) => x.id === e.target.value);
            set("client_id", e.target.value); set("client_nom", c?.raison_sociale || c?.nom_commercial || "");
          }} className="mt-1 w-full h-12 rounded-lg border border-input bg-background px-3 text-sm">
            <option value="">— Choisir —</option>
            {contacts.filter((c) => ["client", "prospect", "donneur_ordre", "collectivite"].includes(c.categorie)).map((c) => (
              <option key={c.id} value={c.id}>{c.raison_sociale}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Chantier</label>
          <select data-testid="quote-site" value={q.site_id} onChange={(e) => {
            const s = sites.find((x) => x.id === e.target.value);
            set("site_id", e.target.value); set("site_ref", s ? `${s.numero} — ${s.nom}` : "");
            if (s?.territoire) set("territoire", s.territoire);
          }} className="mt-1 w-full h-12 rounded-lg border border-input bg-background px-3 text-sm">
            <option value="">— Aucun —</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.numero} — {s.nom}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Territoire (TVA)</label>
          <select data-testid="quote-territoire" value={q.territoire} onChange={(e) => set("territoire", e.target.value)} className="mt-1 w-full h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
            {TERRITOIRES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Objet</label>
          <Input data-testid="quote-objet" value={q.objet} onChange={(e) => set("objet", e.target.value)} className="mt-1 h-12" placeholder="Objet du devis" />
        </div>
      </div>

      <div className="space-y-3" data-testid="quote-items">
        {q.items.map((it, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4 space-y-2" data-testid={`quote-item-${i}`}>
            <div className="flex gap-2">
              <Input data-testid={`item-designation-${i}`} placeholder="Désignation *" value={it.designation} onChange={(e) => setItem(i, "designation", e.target.value)} className="flex-1 font-semibold" />
              <select data-testid={`item-pricebook-${i}`} value="" onChange={(e) => addFromPriceBook(i, e.target.value)} className="h-10 rounded-lg border border-input bg-background px-2 text-xs w-36">
                <option value="">+ Price book…</option>
                {prices.map((p) => <option key={p.id} value={p.reference}>{p.produit} ({eur(p.prix_ht)})</option>)}
              </select>
              <button data-testid={`item-delete-${i}`} onClick={() => setQ((p) => ({ ...p, items: p.items.filter((_, x) => x !== i) }))}
                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"><Trash2 className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 text-xs">
              {[["quantite", "Qté"], ["unite", "Unité"], ["prix_achat", "Achat €"], ["cout_mo", "MO €"], ["cout_materiel", "Matériel €"],
                ["cout_transport", "Transport €"], ["cout_sous_traitance", "Sous-trait. €"], ["autres_couts", "Autres €"], ["marge_pct", "Marge %"]].map(([k, l]) => (
                <div key={k}>
                  <label className="font-bold text-muted-foreground uppercase text-[10px]">{l}</label>
                  <Input data-testid={`item-${k}-${i}`} type={k === "unite" ? "text" : "number"} step="any" value={it[k]}
                    onChange={(e) => setItem(i, k, k === "unite" ? e.target.value : e.target.value)} className="h-10 mt-0.5" />
                </div>
              ))}
            </div>
            <div className="text-xs text-muted-foreground font-mono-num">
              Revient : {eur(((+it.prix_achat || 0) + (+it.cout_mo || 0) + (+it.cout_materiel || 0) + (+it.cout_transport || 0) + (+it.cout_sous_traitance || 0) + (+it.autres_couts || 0)))}
              {" → Vente HT : "}{eur(((+it.prix_achat || 0) + (+it.cout_mo || 0) + (+it.cout_materiel || 0) + (+it.cout_transport || 0) + (+it.cout_sous_traitance || 0) + (+it.autres_couts || 0)) * (1 + (+it.marge_pct || 0) / 100))}
            </div>
          </div>
        ))}
        <Button data-testid="add-item" variant="outline" onClick={() => setQ((p) => ({ ...p, items: [...p.items, { ...EMPTY_ITEM }] }))} className="w-full h-12 font-bold">
          <Plus className="h-4 w-4 mr-1" /> Ajouter une ligne
        </Button>
      </div>

      <div className="bg-card rounded-xl border border-border p-4 grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Remise globale (%)</label>
          <Input data-testid="quote-remise" type="number" step="any" value={q.remise_pct} onChange={(e) => set("remise_pct", e.target.value)} className="mt-1 h-12" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" data-testid="quote-tva-manuel" checked={q.tva_manuel} onChange={(e) => set("tva_manuel", e.target.checked)} />
          <label className="text-sm font-semibold">TVA manuelle</label>
          {q.tva_manuel && <Input data-testid="quote-tva-taux" type="number" step="any" value={q.tva_taux ?? 8.5} onChange={(e) => set("tva_taux", e.target.value)} className="h-10 w-24" />}
        </div>
        {!isNew && (
          <div>
            <label className="text-xs font-bold uppercase text-muted-foreground">Statut</label>
            <select data-testid="quote-statut" value={q.statut} onChange={async (e) => {
              await api.post(`/quotes/${id}/statut`, { statut: e.target.value }).catch(() => {});
              set("statut", e.target.value); toast.success("Statut mis à jour");
            }} className="mt-1 w-full h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
              {STATUTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      {!isNew && (
        <div className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid="signature-section">
          {q.signature_nom ? (
            <div data-testid="signature-done" className="bg-emerald-50 dark:bg-emerald-950 border-2 border-emerald-500 rounded-lg p-4">
              <div className="font-bold text-emerald-800 dark:text-emerald-200 text-sm">
                ✓ Signé électroniquement par {q.signature_nom} — {q.signature_date ? new Date(q.signature_date).toLocaleString("fr-FR") : ""}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Même valeur juridique qu'une signature manuscrite (article 1367 du Code civil).</div>
              {q.signature_data && <img src={q.signature_data} alt="Signature" className="mt-2 h-16 bg-white rounded-lg border border-border" />}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 font-display font-bold text-lg uppercase">
                <PenLine className="h-5 w-5 text-amber-600" /> Signer le devis
              </div>
              <p className="text-xs text-muted-foreground">Signez ci-dessous pour valider. Signature électronique — même valeur juridique qu'une signature manuscrite (article 1367 du Code civil).</p>
              <Input data-testid="signature-nom" placeholder="Nom complet *" value={signNom} onChange={(e) => setSignNom(e.target.value)} className="py-3.5" />
              <canvas ref={canvasRef} width={600} height={200} data-testid="signature-canvas"
                className="w-full h-44 bg-white border-2 border-dashed border-border rounded-xl touch-none cursor-crosshair"
                onPointerDown={drawStart} onPointerMove={drawMove} onPointerUp={drawEnd} onPointerLeave={drawEnd} />
              <div className="flex gap-2">
                <Button variant="outline" data-testid="signature-clear" onClick={clearCanvas} className="h-12 font-bold">Effacer</Button>
                <Button data-testid="signature-submit" onClick={sign} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                  SIGNER ET ACCEPTER LE DEVIS
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-teca-900 text-white rounded-xl p-5 space-y-1.5" data-testid="quote-totals">
        <div className="flex justify-between text-sm"><span className="text-slate-400">Total HT</span><span className="font-mono-num font-bold" data-testid="total-ht">{eur(preview.ht)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">TVA {preview.taux}% {q.territoire === "Guyane" && "(non applicable)"}</span><span className="font-mono-num font-bold" data-testid="total-tva">{eur(preview.tva)}</span></div>
        <div className="flex justify-between text-xl border-t border-amber-500 pt-2"><span className="font-display font-bold uppercase">Total TTC</span><span className="font-mono-num font-extrabold text-amber-400" data-testid="total-ttc">{eur(preview.ttc)}</span></div>
        {q.territoire !== "Guyane" && <div className="text-[10px] text-slate-500">Taux TVA lié à une règle versionnée. À valider comptablement si l'opération nécessite interprétation.</div>}
      </div>
    </div>
  );
}
